import React, { useState } from 'react';
import './SeoEnricherSections.css';
import {
  SeoEnricherFaq,
  SeoEnricherSection
} from '../../utils/seoEnricherPreview';
import { isSeoEnricherIntent } from '../../constants/seoEnricherIntents';
import { getAppEnv } from '../../../config/env';

import {
  getSeoEnricherIntentIconAlt,
  getSeoEnricherIntentIconPath
} from './intentIconPaths';

const env = getAppEnv();
const { PUBLIC_URL } = env.raw;

const SectionFaqs = ({
  faqs,
  heading
}: {
  faqs: SeoEnricherFaq[];
  heading: string;
}) => {
  const validFaqs = faqs.filter(
    faq => faq.question?.trim() && faq.answer?.trim()
  );
  const trimmedHeading = heading.trim();

  if (!validFaqs.length) return null;

  return (
    <div className="inclExcl__faqs">
      {trimmedHeading ? (
        <h3 className="inclExcl__faqSubtitle latoBold">{trimmedHeading}</h3>
      ) : null}
      <div className="inclExcl__faqList">
        {validFaqs.map((faq, faqIdx) => (
          <div key={`faq-${faqIdx}`} className="inclExcl__faqItem">
            <p className="inclExcl__faqQuestion">{faq.question}</p>
            <div
              className="inclExcl__faqAnswer"
              dangerouslySetInnerHTML={{ __html: faq.answer }}
            />
          </div>
        ))}
      </div>
    </div>
  );
};

type SeoEnricherSectionsProps = {
  sourceCityName?: string;
  destinationCityName?: string;
  /**
   * Pre-resolved sections (token replacement / source selection happens in
   * the parent, e.g. RouteTemplate).
   */
  sections?: SeoEnricherSection[];
};

const SeoEnricherSections = ({
  sourceCityName,
  destinationCityName,
  sections
}: SeoEnricherSectionsProps) => {
  const previewSections = sections && sections.length ? sections : [];

  // All panels start closed on SSR and client (no hydration mismatch).
  // Content stays in the DOM for SEO/GEO; CSS collapses via max-height.
  // aria-hidden handles screen-reader visibility when collapsed.
  const [openSet, setOpenSet] = useState<Set<number>>(() => new Set());

  if (!previewSections.length) return null;

  const title = destinationCityName
    ? `${sourceCityName} to ${destinationCityName} Cab Booking Guide on MakeMyTrip`
    : sourceCityName
    ? `${sourceCityName} Cab Booking Guide on MakeMyTrip`
    : 'Cab Booking Guide on MakeMyTrip';

  const toggleSection = (idx: number) => {
    setOpenSet(prev => {
      const next = new Set(prev);
      if (next.has(idx)) {
        next.delete(idx);
      } else {
        next.add(idx);
      }
      return next;
    });
  };

  const handleItemKeyDown = (
    event: React.KeyboardEvent<HTMLDivElement>,
    idx: number
  ) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      toggleSection(idx);
    }
  };

  return (
    <section className="inclExcl">
      <div className="inclExcl__header">
        <h2 className="inclExcl__title latoBold">{title}</h2>
      </div>

      <div className="inclExcl__grid">
        {previewSections.map((section, idx) => {
          const resolvedIntent =
            section.intent && isSeoEnricherIntent(section.intent)
              ? section.intent
              : null;
          const intentIconPath = getSeoEnricherIntentIconPath(resolvedIntent);
          const intentIconUrl = `${PUBLIC_URL}/static/public${intentIconPath}`;
          const intentIconAlt = getSeoEnricherIntentIconAlt(
            resolvedIntent,
            section.title
          );
          const hasContent = Boolean(section.content);
          const isOpen = openSet.has(idx);
          const itemId = resolvedIntent || `section-${idx}`;
          const panelId = `seo-enricher-panel-${itemId}`;

          return (
            <div
              className={`inclExcl__item${
                hasContent ? ' inclExcl__item--clickable' : ''
              }${isOpen ? ' inclExcl__item--open' : ''}`}
              key={`item-${idx}`}
              id={itemId}
              {...(hasContent
                ? {
                    role: 'button' as const,
                    tabIndex: 0,
                    'aria-expanded': isOpen,
                    'aria-controls': panelId,
                    'aria-label': section.title,
                    onClick: () => toggleSection(idx),
                    onKeyDown: (event: React.KeyboardEvent<HTMLDivElement>) =>
                      handleItemKeyDown(event, idx)
                  }
                : {})}
            >
              {hasContent ? (
                <h3 className="inclExcl__itemHeading" id={`${itemId}-trigger`}>
                  <div className="inclExcl__itemMain">
                    <span className="inclExcl__intentIcon">
                      <img
                        className="inclExcl__intentImage"
                        src={intentIconUrl}
                        alt={intentIconAlt}
                        aria-hidden="true"
                      />
                    </span>
                    <span className="inclExcl__itemBody">
                      <span className="inclExcl__itemTitle latoBold">
                        {section.title}
                      </span>
                      <span className="inclExcl__itemSubtitle latoRegular">
                        {section.subtitle}
                      </span>
                    </span>
                    <span className="inclExcl__chevron" aria-hidden="true" />
                  </div>
                </h3>
              ) : (
                <div className="inclExcl__itemMain">
                  <span className="inclExcl__intentIcon">
                    <img
                      className="inclExcl__intentImage"
                      src={intentIconUrl}
                      alt={intentIconAlt}
                    />
                  </span>
                  <div className="inclExcl__itemBody">
                    <h3 className="inclExcl__itemTitle latoBold">
                      {section.title}
                    </h3>
                    <p className="inclExcl__itemSubtitle latoRegular">
                      {section.subtitle}
                    </p>
                  </div>
                </div>
              )}
              {/* Always in the HTML for SEO/GEO. Collapsed via max-height; aria-hidden for a11y. */}
              {hasContent ? (
                <div
                  id={panelId}
                  role="region"
                  aria-labelledby={`${itemId}-trigger`}
                  aria-hidden={!isOpen}
                  className={`inclExcl__infoBox${
                    isOpen ? '' : ' inclExcl__infoBox--hidden'
                  }`}
                >
                  <div dangerouslySetInnerHTML={{ __html: section.content }} />
                  <SectionFaqs
                    faqs={section.faqs}
                    heading={
                      section.title.trim()
                        ? `FAQs — ${section.title.trim()}`
                        : 'FAQs'
                    }
                  />
                </div>
              ) : null}
            </div>
          );
        })}
      </div>
    </section>
  );
};

export default SeoEnricherSections;
